import * as React from "react";
import { colors } from "../../styles";
import { getOnboardData, saveOnboardData } from "../../libs/onboard";
import produce from "immer";
import HardwareHaaS from "./HardwareHaaS";
import HardwarePurchase from "./HardwarePurchase";
import { createTicket } from "../../libs/chartec_cw";
import * as Sentry from "@sentry/browser";

interface IProps extends IOnboardStepProps {}

type HardwareOption = IOnboardDataAttributes["hardwareOption"];

interface IState {
  hardwareOption?: HardwareOption;
  hardwareFiles?: IOnboardDataAttributes["hardwareFiles"];
  hardwareData?: IOnboardDataAttributes["hardwareData"];
  loading: boolean;
}

class HardwareSignup extends React.Component<IProps, IState> {
  state: Readonly<IState> = {
    loading: true,
    hardwareData: {},
    hardwareFiles: [],
  };

  componentDidMount() {
    this.props.allowNextStep(true);
    this.props.onBeforeNextStep(() => {
      this.props.setStepComplete();
    });

    this.fetchData();
  }

  fetchData = () => {
    getOnboardData(this.props.org).then(resp => {
      this.setState(
        produce<IState>(draft => {
          if (resp.data) {
            if (resp.data.attributes.hardwareOption) {
              draft.hardwareOption = resp.data.attributes.hardwareOption;
            }
            if (resp.data.attributes.hardwareFiles) {
              draft.hardwareFiles = resp.data.attributes.hardwareFiles;
            }
            if (resp.data.attributes.hardwareData) {
              draft.hardwareData = resp.data.attributes.hardwareData;
            }
          }
          draft.loading = false;
        })
      );
    });
  };

  chooseOption = (hardwareOption: HardwareOption) => {
    this.setState({ hardwareOption });
    saveOnboardData(this.props.org, { hardwareOption });
  };

  updateHardwareData = (
    hardwareData: IOnboardDataAttributes["hardwareData"]
  ) => {
    this.setState({ hardwareData });
    saveOnboardData(this.props.org, { hardwareData });
  };

  createCWTicket = (acceptedFiles: File[], origin: string) => {
    const { cwCompanyid, cwRecid } = this.props.org.attributes.connectWise;
    const { url } = this.props.org.links;
    // Early exit if no data
    if (!Array.isArray(acceptedFiles) || acceptedFiles.length === 0) {
      return;
    }
    // Format the message
    const params = {
      companyid: cwCompanyid,
      subject: `Core - New Hardware File Uploaded`,
      message: `Member has uploaded new hardware file: ${
        acceptedFiles[0].name
      }\n\nOption selected: ${origin} hardware from Chartec\n\nSee all uploaded files at (must be logged in as admin mode): ${url}#onboarding_data`,
      board: "sales",
      recid: Number(cwRecid),
    };
    createTicket(params).catch(e => Sentry.captureException(e));
  };

  getChoiceComponent = () => {
    const resetChoice = () => this.chooseOption(null);

    const commonProps = {
      org: this.props.org,
      resetChoice,
      hardwareData: this.state.hardwareData,
      hardwareFiles: this.state.hardwareFiles,
      fetchHardwareData: this.fetchData,
      updateHardwareData: this.updateHardwareData,
      createCWTicket: this.createCWTicket,
    };

    switch (this.state.hardwareOption) {
      case "haas":
        return <HardwareHaaS {...commonProps} />;
      case "purchase":
        return <HardwarePurchase {...commonProps} />;
      case "opt_out":
        return (
          <div>
            <div className="panel panel-info">
              <div className="panel-heading">Opted-Out</div>
              <div className="panel-body">
                <p>
                  You chose to opt out of HaaS and hardware purchases. If you'd
                  like to change this later, just return here and click the
                  button below.
                </p>
                <button
                  className={"btn btn-theme-primary"}
                  onClick={resetChoice}
                >
                  Return to Hardware Options
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  };

  render() {
    const { loading, hardwareOption } = this.state;

    if (loading) {
      return <div />;
    }

    return (
      <>
        {hardwareOption ? (
          this.getChoiceComponent()
        ) : (
          <>
            <div className="panel panel-primary">
              <div className="panel-heading">Hardware Signup</div>
              <div className="panel-body">
                <p>
                  Signup to purchase or HaaS hardware from CharTec here. Choose
                  an option to begin.
                </p>
                <p>
                  If you choose to opt-out now, you may return here to change
                  it.
                </p>
                <p>Proceed to the next step when you're ready.</p>
              </div>
            </div>
            <div
              className="panel panel-default"
              style={{ cursor: "pointer" }}
              onClick={() => this.chooseOption("haas")}
            >
              <div className="panel-body">
                <div className="media">
                  <div className="media-left">
                    <a href="javascript:void(0)">
                      <i className="media-object fa fa-server fa-3x fa-fw" />
                    </a>
                  </div>
                  <div className="media-body">
                    <h4 className="media-heading">CharTec HaaS</h4>I want to
                    HaaS hardware with CharTec.
                  </div>
                </div>
              </div>
            </div>
            <div
              className="panel panel-default"
              style={{ cursor: "pointer" }}
              onClick={() => this.chooseOption("purchase")}
            >
              <div className="panel-body">
                <div className="media">
                  <div className="media-left">
                    <a href="javascript:void(0)">
                      <i className="media-object fa fa-usd fa-3x fa-fw" />
                    </a>
                  </div>
                  <div className="media-body">
                    <h4 className="media-heading">Purchase from CharTec</h4>I
                    want to purchase hardware from CharTec.
                  </div>
                </div>
              </div>
            </div>
            <div
              className="panel panel-default"
              style={{ cursor: "pointer" }}
              onClick={() => this.chooseOption("opt_out")}
            >
              <div className="panel-body">
                <div className="media">
                  <div className="media-left">
                    <a href="javascript:void(0)">
                      <i className="media-object fa fa-times-circle-o fa-3x fa-fw" />
                    </a>
                  </div>
                  <div className="media-body">
                    <h4 className="media-heading">Not Now</h4>I don't want to
                    HaaS or purchase hardware through CharTec right now.
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }
}

export default HardwareSignup;



// WEBPACK FOOTER //
// ./app/javascript/components/onboarding/HardwareSignup.tsx